// apps/web/src/components/aiImport/ImportProgress.tsx
// AI 导入进度展示组件：步骤 + 进度条 + 实时 AI 日志 + 分块识别进度

import { useMemo } from "react";
import type { AIJobProgress } from "@/api/aiImport";

const PROGRESS_META: Record<
  AIJobProgress,
  { label: string; percent: number; color: "blue" | "green" | "red" }
> = {
  parsing: { label: "准备文档中", percent: 10, color: "blue" },
  creating_bank: { label: "创建题库中", percent: 25, color: "blue" },
  ai_extracting: { label: "AI 识别题目中", percent: 50, color: "blue" },
  writing: { label: "写入题目中", percent: 80, color: "blue" },
  completed: { label: "已完成", percent: 100, color: "green" },
  failed: { label: "失败", percent: 100, color: "red" },
};

const FALLBACK_PROGRESS: ParsedProgress = {
  phase: "parsing",
  label: "准备文档中",
  percent: 10,
  color: "blue",
};

export interface ParsedProgress {
  phase: AIJobProgress;
  label: string;
  percent: number;
  color: "blue" | "green" | "red";
  chunkIndex?: number;
  chunkTotal?: number;
}

export function parseProgress(progress?: string | null): ParsedProgress {
  const rawPhase = progress?.split(":")[0] ?? "";
  const basePhase: AIJobProgress = (rawPhase as AIJobProgress) || "parsing";
  const meta = PROGRESS_META[basePhase] ?? FALLBACK_PROGRESS;

  let chunkIndex: number | undefined;
  let chunkTotal: number | undefined;
  let extraPercent = 0;

  if (progress && progress.includes(":")) {
    const parts = progress.split(":");
    if (parts.length >= 2 && parts[1].includes("/")) {
      const [a, b] = parts[1].split("/").map(Number);
      if (!Number.isNaN(a) && !Number.isNaN(b) && b > 0) {
        chunkIndex = a;
        chunkTotal = b;
        // 在 ai_extracting 阶段内（50% ~ 80%）按块细分
        if (basePhase === "ai_extracting") {
          extraPercent = ((a - 1) / b) * 30;
        }
      }
    }
  }

  return {
    phase: basePhase,
    label: meta.label,
    percent: Math.min(100, Math.round(meta.percent + extraPercent)),
    color: meta.color,
    chunkIndex,
    chunkTotal,
  };
}

export function getProgressMeta(progress?: string | null): ParsedProgress {
  return parseProgress(progress) ?? FALLBACK_PROGRESS;
}

interface ImportProgressProps {
  progress?: string | null;
  elapsed?: number;
  showPercent?: boolean;
  /** AI 流式日志文本 */
  logs?: string | null;
  /** 是否高亮显示实时日志（在详情页中展开） */
  showLogs?: boolean;
}

export function formatTime(ms: number) {
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}秒`;
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${min}分${rem}秒`;
}

/** 从日志末尾提取一段可读的片段 */
function formatLogSnippet(logs?: string | null): string {
  if (!logs) return "";
  // 去除多余空白，取最后 200 字符
  const cleaned = logs.replace(/\s+/g, " ").trim();
  return cleaned.slice(-200);
}

/** 根据日志内容给出更友好的状态提示 */
function getLogStatus(progress?: string | null, logs?: string | null): string {
  const { phase } = parseProgress(progress) ?? FALLBACK_PROGRESS;
  if (phase !== "ai_extracting") return "";
  if (!logs) return "等待 AI 响应…";
  const trimmed = logs.trim();
  if (trimmed.includes('"questions"')) return "已识别到题目结构，继续解析…";
  if (trimmed.includes('"content"')) return "正在提取题干内容…";
  if (trimmed.includes('"options"')) return "正在整理选项…";
  if (trimmed.includes('"correct_answer"')) return "正在确认答案…";
  return "AI 正在生成识别结果…";
}

export default function ImportProgress({
  progress,
  elapsed,
  showPercent = true,
  logs,
  showLogs = true,
}: ImportProgressProps) {
  const meta = useMemo(() => parseProgress(progress) ?? FALLBACK_PROGRESS, [progress]);
  const barColor =
    meta.color === "green" ? "bg-green-500" : meta.color === "red" ? "bg-red-500" : "bg-blue-500";

  const snippet = useMemo(() => formatLogSnippet(logs), [logs]);
  const logStatus = useMemo(() => getLogStatus(progress, logs), [progress, logs]);
  const isExtracting = meta.phase === "ai_extracting";

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm font-medium text-gray-700">
          {meta.label}
          {isExtracting && meta.chunkTotal && meta.chunkTotal > 1 && meta.chunkIndex && (
            <span className="ml-2 text-xs text-gray-500">
              (第 {meta.chunkIndex}/{meta.chunkTotal} 段)
            </span>
          )}
        </span>
        {showPercent && <span className="text-sm text-gray-500">{meta.percent}%</span>}
      </div>
      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full ${barColor} rounded-full transition-all duration-700 ease-out`}
          style={{ width: `${meta.percent}%` }}
        />
      </div>
      {typeof elapsed === "number" && elapsed > 0 && (
        <p className="text-xs text-gray-400 mt-1.5">已用 {formatTime(elapsed)}</p>
      )}

      {showLogs && isExtracting && (
        <div className="mt-3 rounded-lg bg-slate-50 border border-slate-100 p-3">
          <div className="flex items-center gap-2 mb-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500" />
            </span>
            <span className="text-xs font-medium text-blue-700">{logStatus || "AI 处理中"}</span>
          </div>
          {snippet ? (
            <div className="font-mono text-[10px] leading-relaxed text-slate-600 break-all max-h-24 overflow-y-auto">
              {snippet}
              <span className="inline-block w-1.5 h-3.5 ml-0.5 bg-blue-500 animate-pulse align-middle" />
            </div>
          ) : (
            <p className="text-xs text-slate-400">AI 正在思考，请稍候…</p>
          )}
        </div>
      )}
    </div>
  );
}
